import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import './Matcheo.css';
import './MatchingModal.css';
import * as XLSX from 'xlsx';

// Cloud Functions Base URL (funciona en producción sin servidor local)
const CLOUD_FUNCTIONS_BASE = 'https://us-central1-abbott-matcher-60171.cloudfunctions.net';

const Matcheo = () => {
  const [excelProducts, setExcelProducts] = useState([]);
  const [matchResults, setMatchResults] = useState({});
  const [rejectedMatches, setRejectedMatches] = useState({}); // NEW: Para almacenar rechazados
  const [loading, setLoading] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [filterText, setFilterText] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [showOnlyMatches, setShowOnlyMatches] = useState(false);
  const [showRejectedMatches, setShowRejectedMatches] = useState(false); // NEW: Control de rechazados
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [pagination, setPagination] = useState({ offset: 0, limit: 50 });
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalUnfilteredProducts, setTotalUnfilteredProducts] = useState(0);
  const [deletingMatch, setDeletingMatch] = useState(null);
  const [selectedMatches, setSelectedMatches] = useState({}); // { codigoPresentacion: [matchIds] }
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveSearchText, setMoveSearchText] = useState('');
  const [moveSearchResults, setMoveSearchResults] = useState([]);
  const [isMoving, setIsMoving] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);

  // Estados para matching en la nube
  const [showMatchingModal, setShowMatchingModal] = useState(false);
  const [showAllJobsModal, setShowAllJobsModal] = useState(false); // NEW: Modal para todos los jobs
  const [allJobs, setAllJobs] = useState([]); // NEW: Lista de todos los jobs
  const [loadingJobs, setLoadingJobs] = useState(false); // NEW: Loading state
  const [matchingConfig, setMatchingConfig] = useState({
    limit: 100,
    offset: 0, // NEW: Agregar offset
    topK: 50,
    minVectorScore: 0.65,
    minConfidence: 85
  });
  const [matchingJob, setMatchingJob] = useState(null);
  const [matchingLogs, setMatchingLogs] = useState([]);
  const [matchingProgress, setMatchingProgress] = useState(null);
  const [isStartingMatching, setIsStartingMatching] = useState(false);
  const [viewingJobId, setViewingJobId] = useState('');

  // Resetear paginación cuando cambia el filtro activo o el modo de solo matches
  useEffect(() => {
    setPagination(prev => ({ ...prev, offset: 0 }));
  }, [activeFilter, showOnlyMatches]);

  // Cargar productos del Excel y matcheos guardados
  useEffect(() => {
    loadExcelProducts();
  }, [pagination.offset, pagination.limit, showOnlyMatches, activeFilter]);

  useEffect(() => {
    loadSavedMatches();
    loadRejectedMatches(); // NEW: Cargar también los rechazados
  }, []);

  const handleSearch = () => {
    setActiveFilter(filterText);
  };

  const handleClearFilter = () => {
    setFilterText('');
    setActiveFilter('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const loadExcelProducts = async () => {
    setLoading(true);
    try {
      const searchParam = activeFilter ? `&search=${encodeURIComponent(activeFilter)}` : '';

      // Usar Cloud Functions según el modo (con/sin matches)
      const endpoint = showOnlyMatches
        ? `${CLOUD_FUNCTIONS_BASE}/getmatchedmaestroproducts?limit=${pagination.limit}&offset=${pagination.offset}${searchParam}`
        : `${CLOUD_FUNCTIONS_BASE}/getmaestroproducts?limit=${pagination.limit}&offset=${pagination.offset}${searchParam}`;

      const response = await fetch(endpoint);
      const result = await response.json();

      if (result.success) {
        // Para productos con matches, la estructura es diferente
        const productsData = showOnlyMatches ? result.products : result.data;

        // Adaptar estructura para que coincida con el formato esperado
        const adaptedProducts = productsData.map(p => ({
          'Codigo Presentación': p.codigo_presentacion,
          'Nombre': p.nombre,
          'Marca': p.marca,
          'Presentación': p.presentacion,
          'Laboratorio': p.laboratorio,
          'Principio Activo': p.principio_activo,
          'Concentración': p.concentracion,
          'Forma Farmaceutica': p.forma_farmaceutica,
          'Via Administracion': p.via_administracion,
          'Tipo Venta': p.tipo_venta,
          'Categoria': p.categoria,
          'Subcategoria': p.subcategoria,
          'Precio Publico': p.precio_publico,
          'Corporation': p.corporation,
          'Product': p.product,
          // Si es de matched products, agregar contadores
          ...(p.match_count ? {
            'match_count': p.match_count,
            'best_confidence': p.best_confidence
          } : {})
        }));

        setExcelProducts(adaptedProducts);
        setTotalProducts(result.total);
        setTotalUnfilteredProducts(result.totalUnfiltered || result.total);
      }
    } catch (error) {
      console.error('Error cargando productos:', error);
    } finally {
      setLoading(false);
    }
  };

  const transformMatch = (match) => ({
    id: match.match_id,
    score: parseFloat(match.score),
    confidence: parseInt(match.confidence),
    ai_reasoning: match.ai_reasoning || 'Sin razonamiento disponible',
    metadata: {
      id_catalogo: match.match_id,
      title: match.match_title,
      marca: match.match_marca,
      sitio: match.match_sitio,
      precio: match.match_precio,
      categoria: match.match_categoria,
      forma_farmaceutica: match.match_forma_farmaceutica,
      principio_activo: match.match_principio_activo,
      presentacion: match.match_presentacion,
      stock: match.match_stock,
      sku: match.match_sku,
      // Datos adicionales del catálogo
      imagen: match.match_imagen,
      precio_original: match.match_precio_original,
      precio_descontado: match.match_precio_descontado,
      precio_venta: match.match_precio_venta,
      descuento_porcentaje: match.match_descuento_porcentaje,
      url: match.match_url,
      concentracion: match.match_concentracion,
      via_administracion: match.match_via_administracion,
      registro_sanitario: match.match_registro_sanitario,
      codigo_barras: match.match_codigo_barras,
      laboratorio: match.match_laboratorio
    }
  });

  const loadSavedMatches = async () => {
    try {
      // Siempre cargar matches para mostrar en los productos
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE}/getsavedmatches?limit=10000`);
      const result = await response.json();

      // Agrupar matches por codigo_presentacion
      const groupedMatches = {};
      result.matches.forEach(match => {
        const key = match.codigo_presentacion;
        if (!groupedMatches[key]) {
          groupedMatches[key] = [];
        }
        groupedMatches[key].push(transformMatch(match));
      });

      // Ordenar matches por precio_venta (de menor a mayor)
      Object.keys(groupedMatches).forEach(key => {
        groupedMatches[key].sort((a, b) => {
          const precioA = a.metadata?.precio_venta;
          const precioB = b.metadata?.precio_venta;

          // Si alguno no tiene precio o es 'N/A', ponerlo al final
          if (!precioA || precioA === 'N/A') return 1;
          if (!precioB || precioB === 'N/A') return -1;

          // Ordenar de menor a mayor
          return Number(precioA) - Number(precioB);
        });
      });

      setMatchResults(groupedMatches);
    } catch (error) {
      console.error('Error cargando matches guardados:', error);
    }
  };

  const ensureMatchesLoaded = async (codigoPresentacion) => {
    if (matchResults[codigoPresentacion] && matchResults[codigoPresentacion].length > 0) {
      return;
    }

    try {
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE}/getsavedmatches?codigo_presentacion=${codigoPresentacion}&limit=50`);
      if (!response.ok) {
        console.error(`Error cargando matches para ${codigoPresentacion}:`, response.status);
        return;
      }

      const result = await response.json();
      if (!Array.isArray(result.matches)) {
        return;
      }

      const formatted = result.matches.map(transformMatch);

      setMatchResults(prev => ({
        ...prev,
        [codigoPresentacion]: formatted
      }));
    } catch (error) {
      console.error(`Error cargando matches para ${codigoPresentacion}:`, error);
    }
  };

  // NEW: Función para cargar matches rechazados por AI
  const loadRejectedMatches = async () => {
    try {
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE}/getrejectedmatches?limit=10000`);
      const result = await response.json();

      // Agrupar rechazados por codigo_presentacion
      const groupedRejected = {};
      result.data.forEach(match => {
        const key = match.codigo_presentacion;
        if (!groupedRejected[key]) {
          groupedRejected[key] = [];
        }
        groupedRejected[key].push({
          id: match.match_id,
          score: parseFloat(match.vector_score / 100), // Normalizar a 0-1
          confidence: match.ai_confidence,
          rejection_reason: match.rejection_reason,
          is_rejected: true, // Flag para identificar
          metadata: {
            id_catalogo: match.match_id,
            title: match.title,
            marca: match.marca,
            sitio: match.sitio,
            categoria: match.categoria,
            forma_farmaceutica: match.forma_farmaceutica,
            principio_activo: match.principio_activo,
            presentacion: match.presentacion,
            concentracion: match.concentracion,
            via_administracion: match.via_administracion,
            // Datos de precios e imagen
            imagen: match.imagen,
            precio_original: match.precio_original,
            precio_descontado: match.precio_descontado,
            precio_venta: match.precio_venta,
            url: match.url
          }
        });
      });

      // Ordenar por vector_score (de mayor a menor para rechazados)
      Object.keys(groupedRejected).forEach(key => {
        groupedRejected[key].sort((a, b) => b.score - a.score);
      });

      setRejectedMatches(groupedRejected);
    } catch (error) {
      console.error('Error cargando matches rechazados:', error);
    }
  };

  // Funciones para manejar selección de matches
  const toggleMatchSelection = (codigoPresentacion, matchId) => {
    setSelectedMatches(prev => {
      const current = prev[codigoPresentacion] || [];
      const isSelected = current.includes(matchId);

      if (isSelected) {
        const updated = current.filter(id => id !== matchId);
        if (updated.length === 0) {
          const newState = { ...prev };
          delete newState[codigoPresentacion];
          return newState;
        }
        return { ...prev, [codigoPresentacion]: updated };
      } else {
        return { ...prev, [codigoPresentacion]: [...current, matchId] };
      }
    });
  };

  const isMatchSelected = (codigoPresentacion, matchId) => {
    return (selectedMatches[codigoPresentacion] || []).includes(matchId);
  };

  const getSelectedCount = () => {
    return Object.values(selectedMatches).reduce((sum, ids) => sum + ids.length, 0);
  };

  const hasSelectedRejectedMatches = () => {
    // Verifica si al menos un match seleccionado es rechazado
    for (const [codigoPresentacion, matchIds] of Object.entries(selectedMatches)) {
      const rejected = rejectedMatches[codigoPresentacion] || [];
      if (matchIds.some(matchId => rejected.find(m => m.id === matchId))) {
        return true;
      }
    }
    return false;
  };

  const clearSelection = () => {
    setSelectedMatches({});
  };

  // Función para borrar matches seleccionados
  const deleteSelectedMatches = async () => {
    setShowDeleteModal(false);

    try {
      const affectedProducts = new Set();

      // Eliminar cada match seleccionado
      for (const [codigoPresentacion, matchIds] of Object.entries(selectedMatches)) {
        affectedProducts.add(codigoPresentacion);

        for (const matchId of matchIds) {
          const response = await fetch(
            `${CLOUD_FUNCTIONS_BASE}/deletematch`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ codigoPresentacion, matchId })
            }
          );

          if (!response.ok) {
            console.error(`Error eliminando match ${matchId}`);
          }
        }
      }

      // Después de eliminar todos, actualizar el estado
      setMatchResults(prev => {
        const updated = { ...prev };

        // Actualizar cada producto afectado
        for (const codigoPresentacion of affectedProducts) {
          const selectedIds = selectedMatches[codigoPresentacion] || [];

          if (updated[codigoPresentacion]) {
            // Filtrar los matches eliminados
            updated[codigoPresentacion] = updated[codigoPresentacion].filter(
              m => !selectedIds.includes(m.id)
            );

            // Si no quedan matches, eliminar la entrada
            if (updated[codigoPresentacion].length === 0) {
              delete updated[codigoPresentacion];
            }
          }
        }

        return updated;
      });

      // Si estamos en modo "solo matcheos", eliminar productos sin matches
      if (showOnlyMatches) {
        setExcelProducts(prev => {
          return prev.filter(p => {
            const codigo = p['Codigo Presentación'];
            // Verificar si este producto todavía tiene matches después de la eliminación
            const remainingMatches = (matchResults[codigo] || []).filter(
              m => !selectedMatches[codigo]?.includes(m.id)
            );
            return remainingMatches.length > 0;
          });
        });
      }

      // Colapsar productos que ya no tienen matches
      setExpandedProducts(prev => {
        const newSet = new Set(prev);
        for (const codigoPresentacion of affectedProducts) {
          const remainingMatches = (matchResults[codigoPresentacion] || []).filter(
            m => !selectedMatches[codigoPresentacion]?.includes(m.id)
          );
          if (remainingMatches.length === 0) {
            newSet.delete(codigoPresentacion);
          }
        }
        return newSet;
      });

      clearSelection();
    } catch (error) {
      console.error('Error eliminando matches:', error);
      alert('Error eliminando matches');
    }
  };

  // Función para aprobar rechazados masivamente
  const approveSelectedRejectedMatches = async () => {
    if (!window.confirm(`¿Aprobar ${getSelectedCount()} matches rechazados? Se moverán a aprobados.`)) {
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      // Aprobar cada match seleccionado
      for (const [codigoPresentacion, matchIds] of Object.entries(selectedMatches)) {
        for (const matchId of matchIds) {
          // Buscar el match en rejectedMatches
          const rejected = rejectedMatches[codigoPresentacion] || [];
          const match = rejected.find(m => m.id === matchId);

          if (!match) {
            console.error(`Match ${matchId} no encontrado en rechazados`);
            errorCount++;
            continue;
          }

          const response = await fetch(
            `${CLOUD_FUNCTIONS_BASE}/approverejectedmatch`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                codigoPresentacion,
                matchId: match.id,
                matchData: match.metadata
              })
            }
          );

          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            console.error(`Error aprobando match ${matchId}`);
          }
        }
      }

      // Actualizar estados después de aprobar todos
      if (successCount > 0) {
        // Remover de rechazados
        setRejectedMatches(prev => {
          const updated = { ...prev };
          for (const [codigoPresentacion, matchIds] of Object.entries(selectedMatches)) {
            if (updated[codigoPresentacion]) {
              updated[codigoPresentacion] = updated[codigoPresentacion].filter(
                m => !matchIds.includes(m.id)
              );
              if (updated[codigoPresentacion].length === 0) {
                delete updated[codigoPresentacion];
              }
            }
          }
          return updated;
        });

        // Agregar a aprobados
        setMatchResults(prev => {
          const updated = { ...prev };
          for (const [codigoPresentacion, matchIds] of Object.entries(selectedMatches)) {
            const rejected = rejectedMatches[codigoPresentacion] || [];
            const matchesToAdd = rejected.filter(m => matchIds.includes(m.id));

            if (!updated[codigoPresentacion]) {
              updated[codigoPresentacion] = [];
            }

            matchesToAdd.forEach(match => {
              updated[codigoPresentacion].push({
                id: match.id,
                score: match.score,
                confidence: 85, // Confidence fijo para validaciones manuales
                metadata: match.metadata
              });
            });
          }
          return updated;
        });
      }

      clearSelection();

      if (errorCount > 0) {
        alert(`${successCount} matches aprobados correctamente. ${errorCount} errores.`);
      } else {
        alert(`${successCount} matches aprobados correctamente`);
      }
    } catch (error) {
      console.error('Error aprobando matches:', error);
      alert('Error aprobando matches rechazados');
    }
  };

  // Función para buscar productos destino
  const searchMoveDestination = async (searchText) => {
    // TEMPORALMENTE DESHABILITADO - Requiere servidor local
    console.log('Búsqueda de productos deshabilitada - requiere servidor local');
    setMoveSearchResults([]);
    return;

    /* CÓDIGO ORIGINAL COMENTADO
    if (!searchText || searchText.length < 2) {
      setMoveSearchResults([]);
      return;
    }

    try {
      const response = await fetch(
        `http://localhost:5001/api/matcheo/excel-products?limit=20&offset=0&search=${encodeURIComponent(searchText)}`
      );
      const result = await response.json();
      setMoveSearchResults(result.data || []);
    } catch (error) {
      console.error('Error buscando productos:', error);
    }
    */
  };

  // Función para confirmar y mover matches seleccionados
  const confirmMoveMatches = async () => {
    // TEMPORALMENTE DESHABILITADO - Requiere servidor local
    alert('Función de mover matches temporalmente deshabilitada en modo cloud-only');
    setIsMoving(false);
    return;

    /* CÓDIGO ORIGINAL COMENTADO
    if (!selectedDestination) return;

    setIsMoving(true);
    try {
      const destinationCode = selectedDestination['Codigo Presentación'];
      const destinationName = selectedDestination['Presentación'];

      console.log('Moviendo matches a:', destinationCode);
      console.log('Matches seleccionados:', selectedMatches);

      const response = await fetch('http://localhost:5001/api/matcheo/move-matches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selectedMatches,
          destinationCode
        })
      });

      const result = await response.json();

      if (response.ok) {
        console.log('Matches movidos exitosamente:', result);
        // Recargar matches y productos
        await loadSavedMatches();
        await loadExcelProducts();
        clearSelection();
        setShowMoveModal(false);
        setMoveSearchText('');
        setMoveSearchResults([]);
        setSelectedDestination(null);

        // Construir mensaje apropiado
        let message = '';
        if (result.movedCount > 0 && result.skippedCount > 0) {
          message = `✓ ${result.movedCount} match${result.movedCount > 1 ? 'es' : ''} movido${result.movedCount > 1 ? 's' : ''} a "${destinationName}"\n\n${result.skippedCount} match${result.skippedCount > 1 ? 'es' : ''} ya existía${result.skippedCount > 1 ? 'n' : ''} en el destino y ${result.skippedCount > 1 ? 'fueron eliminados' : 'fue eliminado'} del origen para evitar duplicados.`;
        } else if (result.movedCount > 0) {
          message = `✓ ${result.movedCount} match${result.movedCount > 1 ? 'es' : ''} movido${result.movedCount > 1 ? 's' : ''} exitosamente a "${destinationName}"`;
        } else if (result.skippedCount > 0) {
          message = `ℹ️ Los ${result.skippedCount} match${result.skippedCount > 1 ? 'es' : ''} seleccionado${result.skippedCount > 1 ? 's' : ''} ya existían en "${destinationName}".\n\n${result.skippedCount > 1 ? 'Fueron eliminados' : 'Fue eliminado'} del producto origen para evitar duplicados.`;
        }

        alert(message);
      } else {
        console.error('Error en la respuesta:', result);
        alert(result.error || 'Error moviendo matches');
      }
    } catch (error) {
      console.error('Error moviendo matches:', error);
      alert('Error moviendo matches: ' + error.message);
    } finally {
      setIsMoving(false);
    }
    */
  };

  // Función para eliminar un match
  const deleteMatch = async (codigoPresentacion, matchId) => {
    if (!window.confirm('¿Estás seguro de eliminar este match?')) {
      return;
    }

    // Marcar el match como "deleting" para aplicar la animación
    setDeletingMatch(`${codigoPresentacion}-${matchId}`);

    try {
      const response = await fetch(
        `${CLOUD_FUNCTIONS_BASE}/deletematch`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ codigoPresentacion, matchId })
        }
      );

      if (response.ok) {
        // Esperar a que termine la animación (300ms)
        setTimeout(() => {
          // Actualizar estado local inmediatamente sin recargar todo
          setMatchResults(prev => {
            const updated = { ...prev };
            if (updated[codigoPresentacion]) {
              updated[codigoPresentacion] = updated[codigoPresentacion].filter(
                m => m.id !== matchId
              );
              // Si ya no tiene matches, eliminar la entrada
              if (updated[codigoPresentacion].length === 0) {
                delete updated[codigoPresentacion];
              }
            }
            return updated;
          });

          // Si estamos en modo "solo matcheos" y eliminamos todos los matches de un producto,
          // también quitarlo de la lista de productos
          if (showOnlyMatches) {
            const remainingMatches = (matchResults[codigoPresentacion] || []).filter(
              m => m.id !== matchId
            );
            if (remainingMatches.length === 0) {
              setExcelProducts(prev => prev.filter(p => p['Codigo Presentación'] !== codigoPresentacion));
              setTotalProducts(prev => prev - 1);
            }
          }

          setDeletingMatch(null);
        }, 300);
      } else {
        setDeletingMatch(null);
        alert('Error eliminando match');
      }
    } catch (error) {
      console.error('Error eliminando match:', error);
      setDeletingMatch(null);
      alert('Error eliminando match');
    }
  };

  // Función para aprobar un match rechazado
  const approveRejectedMatch = async (codigoPresentacion, match) => {
    if (!window.confirm('¿Aprobar este match? Se moverá de rechazados a aprobados.')) {
      return;
    }

    try {
      // Llamar al endpoint para aprobar el match rechazado
      const response = await fetch(
        `${CLOUD_FUNCTIONS_BASE}/approverejectedmatch`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            codigoPresentacion,
            matchId: match.id,
            matchData: match.metadata
          })
        }
      );

      if (response.ok) {
        // Actualizar estado local: remover de rechazados y agregar a aprobados
        setRejectedMatches(prev => {
          const updated = { ...prev };
          if (updated[codigoPresentacion]) {
            updated[codigoPresentacion] = updated[codigoPresentacion].filter(
              m => m.id !== match.id
            );
            if (updated[codigoPresentacion].length === 0) {
              delete updated[codigoPresentacion];
            }
          }
          return updated;
        });

        setMatchResults(prev => {
          const updated = { ...prev };
          if (!updated[codigoPresentacion]) {
            updated[codigoPresentacion] = [];
          }
          // Agregar el match aprobado con confidence de 85% (validación manual)
          updated[codigoPresentacion].push({
            id: match.id,
            score: match.score,
            confidence: 85, // Confidence fijo para validaciones manuales
            metadata: match.metadata
          });
          return updated;
        });

        alert('Match aprobado correctamente');
      } else {
        const error = await response.json();
        alert(`Error aprobando match: ${error.error || 'Error desconocido'}`);
      }
    } catch (error) {
      console.error('Error aprobando match:', error);
      alert('Error aprobando match');
    }
  };

  // Función para remover un match desde el panel de detalle
  const removeMatch = async (product) => {
    if (!window.confirm(`¿Remover este match?\n\nProducto: ${product.title}\nProducto Maestro: ${product.maestro_nombre}`)) {
      return;
    }

    try {
      const response = await fetch(
        `${CLOUD_FUNCTIONS_BASE}/deletematch`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ codigoPresentacion: product.maestro_codigo, matchId: product.match_id })
        }
      );

      if (response.ok) {
        // Actualizar estado local
        setMatchResults(prev => {
          const updated = { ...prev };
          if (updated[product.maestro_codigo]) {
            updated[product.maestro_codigo] = updated[product.maestro_codigo].filter(
              m => m.id !== product.match_id
            );
            if (updated[product.maestro_codigo].length === 0) {
              delete updated[product.maestro_codigo];
            }
          }
          return updated;
        });

        // Cerrar el panel
        setSelectedProduct(null);
        alert('Match removido correctamente');
      } else {
        const error = await response.json();
        alert(`Error removiendo match: ${error.error || 'Error desconocido'}`);
      }
    } catch (error) {
      console.error('Error removiendo match:', error);
      alert('Error removiendo match');
    }
  };

  // Función para validar manualmente un match desde el panel de detalle
  const validateManualMatch = async (product) => {
    if (!window.confirm(`¿Validar este producto como match?\n\nProducto TOMA: ${product.title}\nProducto Maestro: ${product.maestro_nombre}`)) {
      return;
    }

    try {
      const response = await fetch(
        `${CLOUD_FUNCTIONS_BASE}/addmanualmatch`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            codigoPresentacion: product.maestro_codigo,
            nombreProducto: product.maestro_nombre,
            marcaExcel: product.maestro_marca || '',
            matchId: product.id_catalogo,
            matchData: {
              title: product.title,
              marca: product.marca,
              sitio: product.sitio,
              categoria: product.categoria,
              forma_farmaceutica: product.forma_farmaceutica,
              principio_activo: product.principio_activo,
              concentracion: product.concentracion,
              presentacion: product.presentacion,
              via_administracion: product.via_administracion,
              registro_sanitario: product.registro_sanitario,
              codigo_barras: product.codigo_barras,
              laboratorio: product.laboratorio,
              precio_original: product.precio_original,
              precio_descontado: product.precio_descontado,
              precio_venta: product.precio_venta,
              imagen: product.imagen,
              url: product.url
            }
          })
        }
      );

      if (response.ok) {
        // Agregar a matchResults en el estado local
        setMatchResults(prev => {
          const updated = { ...prev };
          if (!updated[product.maestro_codigo]) {
            updated[product.maestro_codigo] = [];
          }

          // Evitar duplicados
          const exists = updated[product.maestro_codigo].some(m => m.id === product.id_catalogo);
          if (!exists) {
            updated[product.maestro_codigo].push({
              id: product.id_catalogo,
              score: 0.85,
              confidence: 85,
              metadata: {
                id_catalogo: product.id_catalogo,
                title: product.title,
                marca: product.marca,
                sitio: product.sitio,
                categoria: product.categoria,
                forma_farmaceutica: product.forma_farmaceutica,
                principio_activo: product.principio_activo,
                concentracion: product.concentracion,
                presentacion: product.presentacion,
                via_administracion: product.via_administracion,
                registro_sanitario: product.registro_sanitario,
                codigo_barras: product.codigo_barras,
                laboratorio: product.laboratorio,
                imagen: product.imagen,
                precio_original: product.precio_original,
                precio_descontado: product.precio_descontado,
                precio_venta: product.precio_venta,
                url: product.url,
                ai_reasoning: 'Validación manual desde panel de detalle - Match agregado por el usuario'
              }
            });
          }
          return updated;
        });

        // Si estaba en rechazados, quitarlo de ahí también
        setRejectedMatches(prev => {
          const updated = { ...prev };
          if (updated[product.maestro_codigo]) {
            updated[product.maestro_codigo] = updated[product.maestro_codigo].filter(
              m => m.id !== product.id_catalogo
            );
          }
          return updated;
        });

        // Cerrar el panel
        setSelectedProduct(null);
        alert('Match validado correctamente');
      } else {
        const error = await response.json();
        alert(`Error validando match: ${error.error || 'Error desconocido'}`);
      }
    } catch (error) {
      console.error('Error validando match:', error);
      alert('Error validando match');
    }
  };

  const toggleExpanded = (key) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return '#10B981'; // Verde
    if (confidence >= 80) return '#60A5FA'; // Azul más claro
    if (confidence >= 70) return '#60A5FA'; // Azul más claro
    return '#EF4444'; // Rojo
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 90) return 'Muy Alto';
    if (confidence >= 80) return 'Alto';
    if (confidence >= 70) return 'Medio';
    return 'Bajo';
  };

  // Funciones para matching en la nube
  const startCloudMatching = async () => {
    try {
      setIsStartingMatching(true);
      setMatchingLogs([{ timestamp: Date.now(), type: 'info', message: 'Iniciando proceso de matching en la nube...' }]);

      console.log('Enviando request a Cloud Function...', matchingConfig);

      const response = await fetch(
        'https://us-central1-abbott-matcher-60171.cloudfunctions.net/matchwithai',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(matchingConfig)
        }
      );

      console.log('Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Response data:', data);
        setMatchingJob(data);
        setMatchingLogs(prev => [...prev, {
          timestamp: Date.now(),
          type: 'success',
          message: `Job iniciado: ${data.jobId}`
        }]);

        // Iniciar polling de logs
        startLogPolling(data.jobId);
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setMatchingLogs(prev => [...prev, {
          timestamp: Date.now(),
          type: 'error',
          message: `Error HTTP ${response.status}: ${errorText}`
        }]);
        setIsStartingMatching(false);
      }
    } catch (error) {
      console.error('Error iniciando matching:', error);
      setMatchingLogs(prev => [...prev, {
        timestamp: Date.now(),
        type: 'error',
        message: `Error: ${error.message}`
      }]);
      setIsStartingMatching(false);
    }
  };

  const startLogPolling = (jobId) => {
    console.log('Iniciando polling de logs para jobId:', jobId);
    setIsStartingMatching(false); // Ya podemos quitar el spinner

    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(
          `https://us-central1-abbott-matcher-60171.cloudfunctions.net/getmatchinglogs?jobId=${jobId}`
        );

        if (response.ok) {
          const data = await response.json();
          const jobData = data.data;

          console.log('Logs recibidos:', jobData.logs?.length || 0, 'logs');
          console.log('Progreso:', jobData.progress);

          setMatchingLogs(jobData.logs || []);
          setMatchingProgress(jobData.progress);

          // Si el job terminó, detener el polling
          if (jobData.status === 'completed' || jobData.status === 'error') {
            console.log('Job terminado con status:', jobData.status);
            clearInterval(pollInterval);
            setMatchingJob(prev => ({ ...prev, status: jobData.status }));

            // Recargar matches
            if (jobData.status === 'completed') {
              loadSavedMatches();
            }
          }
        }
      } catch (error) {
        console.error('Error polling logs:', error);
      }
    }, 2000); // Poll cada 2 segundos

    // Guardar el interval ID para poder limpiarlo después
    return () => clearInterval(pollInterval);
  };

  // Función para ver logs de un job existente
  const viewExistingJobLogs = () => {
    if (!viewingJobId.trim()) {
      alert('Por favor ingresa un Job ID');
      return;
    }

    // Simular que tenemos un job para mostrar el modal
    setMatchingJob({ jobId: viewingJobId, status: 'running' });
    setMatchingLogs([{ timestamp: Date.now(), type: 'info', message: 'Cargando logs...' }]);
    setMatchingProgress(null);
    setShowMatchingModal(true);

    // Iniciar polling
    startLogPolling(viewingJobId);
  };

  // NEW: Función para cargar todos los jobs
  const loadAllJobs = async () => {
    setLoadingJobs(true);
    try {
      const response = await fetch(
        'https://us-central1-abbott-matcher-60171.cloudfunctions.net/listalljobs?limit=20'
      );
      const data = await response.json();
      if (data.success) {
        setAllJobs(data.jobs);
      }
    } catch (error) {
      console.error('Error cargando jobs:', error);
    } finally {
      setLoadingJobs(false);
    }
  };

  // NEW: Función para abrir modal de todos los jobs
  const openAllJobsModal = () => {
    setShowAllJobsModal(true);
    loadAllJobs();
  };

  // Función para verificar si un valor es válido (no N/A, no vacío, no null, no undefined)
  const isValidValue = (value) => {
    return value && value !== 'N/A' && value !== '' && value !== null && value !== undefined;
  };

  // Función para resaltar términos
  const highlightText = (text, terms) => {
    if (!text || !terms || terms.length === 0) return text;

    let highlightedText = String(text);
    terms.forEach(term => {
      if (term) {
        const regex = new RegExp(`(${term})`, 'gi');
        highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
      }
    });

    return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
  };

  // Los productos ya vienen filtrados del backend
  const filteredProducts = useMemo(() => {
    if (!excelProducts || !Array.isArray(excelProducts)) return [];
    return excelProducts;
  }, [excelProducts]);

  // Obtener términos de búsqueda
  const searchTerms = useMemo(() => {
    if (!activeFilter) return [];
    return activeFilter.split(',').map(t => t.trim()).filter(t => t);
  }, [activeFilter]);

  const exportToExcel = async () => {
    try {
      // Mostrar indicador de carga
      const originalText = document.querySelector('.export-btn')?.textContent;
      const exportBtn = document.querySelector('.export-btn');
      if (exportBtn) exportBtn.textContent = 'Exportando...';

      // Obtener el total de productos a exportar
      const totalToExport = activeFilter ? totalProducts : totalUnfilteredProducts;

      if (totalToExport === 0) {
        alert('No hay productos para exportar');
        if (exportBtn) exportBtn.textContent = originalText;
        return;
      }

      // Construir la URL con todos los productos (sin paginación)
      const searchParam = activeFilter ? `&search=${encodeURIComponent(activeFilter)}` : '';
      const endpoint = showOnlyMatches
        ? `${CLOUD_FUNCTIONS_BASE}/getmatchedmaestroproducts?limit=${totalToExport}&offset=0${searchParam}`
        : `${CLOUD_FUNCTIONS_BASE}/getmaestroproducts?limit=${totalToExport}&offset=0${searchParam}`;

      // Fetch de TODOS los productos
      const response = await fetch(endpoint);
      const result = await response.json();
      const allProducts = result.data || [];

      // Preparar datos para exportar
      const exportData = [];

      allProducts.forEach(producto => {
        const key = producto['Codigo Presentación'];
        const matches = matchResults[key] || [];
        const rejected = rejectedMatches[key] || [];

        // Incluir rechazados si el toggle está activado
        const allMatches = showRejectedMatches ? [...matches, ...rejected] : matches;

        if (allMatches.length === 0) {
          // Producto maestro sin matches
          exportData.push({
            'Tipo': 'Maestro',
            'Código Maestro': producto['Codigo Presentación'],
            'Presentación Maestro': producto['Presentación'],
            'Product Maestro': producto['Product'],
            'Corporation Maestro': producto['Corporation'],
            'Type Maestro': producto['Type'],
            'Total Matches': 0,
            '': '',
            'Estado Match': '',
            'Título Match': '',
            'Marca Match': '',
            'Sitio Match': '',
            'Precio Original': '',
            'Precio Descontado': '',
            'Precio Venta': '',
            'Similitud %': '',
            'Nivel Confianza': ''
          });
        } else {
          // Producto maestro con sus matches anidados
          allMatches.forEach((match, idx) => {
            const precioOriginal = match.metadata?.precio_original;
            const precioDescontado = match.metadata?.precio_descontado;
            const precioVenta = match.metadata?.precio_venta;

            exportData.push({
              'Tipo': idx === 0 ? 'Maestro' : '',
              'Código Maestro': idx === 0 ? producto['Codigo Presentación'] : '',
              'Presentación Maestro': idx === 0 ? producto['Presentación'] : '',
              'Product Maestro': idx === 0 ? producto['Product'] : '',
              'Corporation Maestro': idx === 0 ? producto['Corporation'] : '',
              'Type Maestro': idx === 0 ? producto['Type'] : '',
              'Total Matches': idx === 0 ? allMatches.length : '',
              '': '',
              'Estado Match': match.is_rejected ? 'Rechazado' : 'Aprobado',
              'Título Match': match.metadata?.title || '',
              'Marca Match': match.metadata?.marca || '',
              'Sitio Match': match.metadata?.sitio || '',
              'Precio Original': precioOriginal && precioOriginal !== 'N/A' ? precioOriginal : '',
              'Precio Descontado': precioDescontado && precioDescontado !== 'N/A' ? precioDescontado : '',
              'Precio Venta': precioVenta && precioVenta !== 'N/A' ? precioVenta : '',
              'Similitud %': match.confidence || Math.round((match.score || 0) * 100),
              'Nivel Confianza': getConfidenceLabel(match.confidence || Math.round((match.score || 0) * 100))
            });
          });
        }
      });

      if (exportData.length === 0) {
        alert('No hay datos para exportar');
        if (exportBtn) exportBtn.textContent = originalText;
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Auto-size columns
      const maxWidth = 50;
      const colWidths = Object.keys(exportData[0]).map(key => {
        const maxLen = Math.max(
          key.length,
          ...exportData.slice(0, 100).map(row => String(row[key] || '').length)
        );
        return { wch: Math.min(maxLen + 2, maxWidth) };
      });
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'Matcheo');
      const fileName = `matcheo_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      // Restaurar texto del botón
      if (exportBtn) exportBtn.textContent = originalText;
    } catch (error) {
      console.error('Error exportando:', error);
      alert('Error al exportar. Por favor intenta de nuevo.');
      const exportBtn = document.querySelector('.export-btn');
      if (exportBtn) exportBtn.textContent = 'Exportar';
    }
  };

  return (
    <div className={`matcheo-page ${selectedProduct ? 'panel-open' : ''}`}>
      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-input-container">
          <div className="filter-input-wrapper">
            <input
              type="text"
              placeholder="Buscar productos... (separar con comas)"
              className="filter-input"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            {filterText && (
              <button
                className="filter-input-clear"
                onClick={handleClearFilter}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                  <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"/>
                </svg>
              </button>
            )}
          </div>
          <button className="search-btn" onClick={handleSearch}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            Buscar
          </button>
          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={showOnlyMatches}
              onChange={(e) => setShowOnlyMatches(e.target.checked)}
            />
            <span>Solo con matches</span>
          </label>
          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={showRejectedMatches}
              onChange={(e) => setShowRejectedMatches(e.target.checked)}
            />
            <span>Mostrar rechazados</span>
          </label>
          <button className="export-btn" onClick={exportToExcel}>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/>
            </svg>
            Exportar
          </button>
        </div>
      </div>

      {/* Products Table */}
      <div className="matcheo-content">
        <div className="products-count-header">
          {activeFilter || showOnlyMatches ? (
            <>
              Mostrando {filteredProducts?.length || 0} de {totalProducts || 0} sobre {totalUnfilteredProducts || 0} productos
            </>
          ) : (
            <>
              Mostrando {filteredProducts?.length || 0} de {totalUnfilteredProducts || 0} productos
            </>
          )}
        </div>
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Cargando productos...</p>
          </div>
        ) : (
          <div className="products-table-wrapper">
            <table className="products-table">
              <thead>
                <tr>
                  <th className="col-matches">Matches</th>
                  <th className="col-codigo">Código</th>
                  <th className="col-presentacion">Presentación</th>
                  <th className="col-product">Product</th>
                  <th className="col-corporation">Corporation</th>
                  <th className="col-type">Type</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts && filteredProducts.map((producto) => {
                  const key = producto['Codigo Presentación'];
                  const approvedMatches = matchResults[key] || [];
                  const rejected = rejectedMatches[key] || [];
                  const backendMatchCount = Number(producto.match_count || 0);
                  // Combinar aprobados y rechazados según el estado del switch
                  const matches = showRejectedMatches
                    ? [...approvedMatches, ...rejected]
                    : approvedMatches;
                  const isExpanded = expandedProducts.has(key);
                  // Permitir expandir si hay aprobados O si hay rechazados y el switch está activado
                  const hasMatches = approvedMatches.length > 0 || backendMatchCount > 0 || (showRejectedMatches && rejected.length > 0);
                  const displayMatchesCount = approvedMatches.length > 0 ? approvedMatches.length : backendMatchCount;
                  const bestConfidenceValue = approvedMatches.length > 0
                    ? (approvedMatches[0]?.confidence || Math.round((approvedMatches[0]?.score || 0) * 100))
                    : (producto.best_confidence ? parseFloat(producto.best_confidence) : 0);

                  return (
                    <React.Fragment key={key}>
                      {/* Fila principal del producto */}
                      <tr
                        className={`product-row ${isExpanded ? 'expanded' : ''} ${hasMatches ? 'has-matches' : ''}`}
                        onClick={async () => {
                          if (!hasMatches) return;
                          // Cargar matches on-demand si aún no están en memoria
                          if (!matchResults[key] || matchResults[key].length === 0) {
                            await ensureMatchesLoaded(key);
                          }
                          toggleExpanded(key);
                        }}
                        style={{ cursor: hasMatches ? 'pointer' : 'default' }}
                      >
                        <td className="col-matches">
                          <div className="matches-indicator">
                            {hasMatches || (showRejectedMatches && rejected.length > 0) ? (
                              <div className="matches-badge" style={{
                                backgroundColor: getConfidenceColor(bestConfidenceValue || 0)
                              }}>
                                <span className="matches-count">
                                  {showRejectedMatches
                                    ? `${displayMatchesCount}/${displayMatchesCount + rejected.length}`
                                    : displayMatchesCount
                                  }
                                </span>
                                <svg width="16" height="16" viewBox="0 0 20 20" fill="white" style={{ marginLeft: '4px' }}>
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                              </div>
                            ) : (
                              <div className="no-matches-icon">
                                {showRejectedMatches && rejected.length > 0 ? `0/${rejected.length}` : '—'}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="col-codigo">{highlightText(producto['Codigo Presentación'], searchTerms)}</td>
                        <td className="col-presentacion">{highlightText(producto['Presentación'], searchTerms)}</td>
                        <td className="col-product">{highlightText(producto['Product'] || 'N/A', searchTerms)}</td>
                        <td className="col-corporation">{highlightText(producto['Corporation'] || 'N/A', searchTerms)}</td>
                        <td className="col-type">{highlightText(producto['Product Type'] || 'N/A', searchTerms)}</td>
                      </tr>

                      {/* Fila expandida con tabla de matches */}
                      {isExpanded && hasMatches && (
                        <tr className="matches-expanded-row">
                          <td colSpan="6">
                            <div className="matches-detail-container">
                              <div className="matches-detail-header">
                                <h4>
                                  {displayMatchesCount} Match{displayMatchesCount !== 1 ? 'es' : ''} encontrado{displayMatchesCount !== 1 ? 's' : ''}
                                  {!showRejectedMatches && rejected.length > 0 && (
                                    <span style={{ marginLeft: '10px', fontSize: '0.85em', opacity: 0.8 }}>
                                      (<span style={{color: '#f59e0b'}}>{rejected.length} rechazados</span>)
                                    </span>
                                  )}
                                </h4>
                                {(approvedMatches[0] || bestConfidenceValue) && bestConfidenceValue > 0 && (
                                  <span>
                                    Mejor similitud: {approvedMatches[0]?.confidence || Math.round(bestConfidenceValue)}% ({getConfidenceLabel(approvedMatches[0]?.confidence || Math.round(bestConfidenceValue))})
                                  </span>
                                )}
                              </div>
                              {getSelectedCount() > 0 && (
                                <div className="matches-actions-bar">
                                  <span className="selected-count">{getSelectedCount()} seleccionado{getSelectedCount() > 1 ? 's' : ''}</span>
                                  <div className="action-buttons">
                                    {hasSelectedRejectedMatches() && (
                                      <button className="action-btn approve-btn" onClick={approveSelectedRejectedMatches}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M20 6L9 17l-5-5"/>
                                        </svg>
                                        Validar
                                      </button>
                                    )}
                                    <button className="action-btn delete-btn" onClick={() => setShowDeleteModal(true)}>
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                      </svg>
                                      Borrar
                                    </button>
                                    <button className="action-btn move-btn" onClick={() => setShowMoveModal(true)}>
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M9 5l7 7-7 7"/>
                                      </svg>
                                      Mover
                                    </button>
                                    <button className="action-btn cancel-btn" onClick={clearSelection}>
                                      Cancelar
                                    </button>
                                  </div>
                                </div>
                              )}
                              <table className="matches-detail-table">
                                <thead>
                                  <tr>
                                    <th><input type="checkbox" disabled style={{opacity: 0}} /></th>
                                    <th></th>
                                    <th>#</th>
                                    <th>Imagen</th>
                                    <th>Título</th>
                                    <th className="price-header">P. Original</th>
                                    <th className="price-header">P. Descontado</th>
                                    <th className="price-header">P. Venta</th>
                                    <th>Sitio</th>
                                    <th>Marca</th>
                                    <th>Categoría</th>
                                    <th>Forma Farm.</th>
                                    <th>Principio Activo</th>
                                    <th>Concentración</th>
                                    <th>Similitud</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {matches.map((match, idx) => {
                                    const precioOriginal = match.metadata?.precio_original;
                                    const precioDescontado = match.metadata?.precio_descontado;
                                    const precioVenta = match.metadata?.precio_venta;
                                    let descuentoPorcentaje = match.metadata?.descuento_porcentaje;

                                    // Calcular descuento si no existe pero hay precio original y descontado
                                    if (!descuentoPorcentaje && precioOriginal && precioOriginal !== 'N/A' && precioDescontado && precioDescontado !== 'N/A') {
                                      const descuento = ((Number(precioOriginal) - Number(precioDescontado)) / Number(precioOriginal)) * 100;
                                      descuentoPorcentaje = Math.round(descuento);
                                    }

                                    const isRejected = match.is_rejected === true;
                                    const rowStyle = isRejected ? {
                                      backgroundColor: '#fef3c7', // Yellow background for rejected
                                      borderLeft: '3px solid #f59e0b'
                                    } : {};

                                    return (
                                      <tr
                                        key={idx}
                                        className={`match-row clickable ${deletingMatch === `${key}-${match.id}` ? 'deleting' : ''} ${isMatchSelected(key, match.id) ? 'selected-row' : ''} ${isRejected ? 'rejected-match' : ''}`}
                                        style={rowStyle}
                                        onClick={() => setSelectedProduct({
                                          ...match.metadata,
                                          is_rejected: isRejected,
                                          is_approved: !isRejected,
                                          match_id: match.id,
                                          rejection_reason: match.rejection_reason,
                                          ai_reasoning: match.ai_reasoning,
                                          // Datos del producto maestro para el botón de validar
                                          maestro_codigo: key,
                                          maestro_nombre: producto['Presentación'],
                                          maestro_marca: producto['Marca']
                                        })}
                                      >
                                        <td className="match-checkbox" onClick={(e) => e.stopPropagation()}>
                                          <input
                                            type="checkbox"
                                            checked={isMatchSelected(key, match.id)}
                                            onChange={() => toggleMatchSelection(key, match.id)}
                                          />
                                        </td>
                                        <td className="match-delete">
                                          {isRejected ? (
                                            <button
                                              className="approve-match-btn"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                approveRejectedMatch(producto['Codigo Presentación'], match);
                                              }}
                                              title="Aprobar match (mover a aprobados)"
                                            >
                                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M20 6L9 17l-5-5"/>
                                              </svg>
                                            </button>
                                          ) : (
                                            <button
                                              className="delete-match-btn"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                deleteMatch(producto['Codigo Presentación'], match.id);
                                              }}
                                              title="Eliminar match"
                                            >
                                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6"/>
                                              </svg>
                                            </button>
                                          )}
                                        </td>
                                        <td className="match-number">#{idx + 1}</td>
                                        <td className="match-image">
                                          {match.metadata?.imagen && match.metadata.imagen !== 'N/A' ? (
                                            <img
                                              src={match.metadata.imagen}
                                              alt={match.metadata?.title || 'Producto'}
                                              onError={(e) => {
                                                e.target.style.display = 'none';
                                              }}
                                            />
                                          ) : (
                                            <span className="no-image">Sin imagen</span>
                                          )}
                                        </td>
                                        <td className="match-title">{highlightText(match.metadata?.title || 'N/A', searchTerms)}</td>
                                        <td className="price-cell">{precioOriginal && precioOriginal !== 'N/A' ? `$${Number(precioOriginal).toFixed(0)}` : ''}</td>
                                        <td className="price-cell">
                                          {precioDescontado && precioDescontado !== 'N/A' ? (
                                            <>
                                              ${Number(precioDescontado).toFixed(0)}
                                              {descuentoPorcentaje && <span className="descuento-badge"> ({descuentoPorcentaje}%)</span>}
                                            </>
                                          ) : ''}
                                        </td>
                                        <td className="price-cell">{precioVenta && precioVenta !== 'N/A' ? `$${Number(precioVenta).toFixed(0)}` : ''}</td>
                                        <td>{highlightText(match.metadata?.sitio || 'N/A', searchTerms)}</td>
                                        <td>{highlightText(match.metadata?.marca || 'N/A', searchTerms)}</td>
                                        <td>{highlightText(match.metadata?.categoria || 'N/A', searchTerms)}</td>
                                        <td>{highlightText(match.metadata?.forma_farmaceutica || 'N/A', searchTerms)}</td>
                                        <td>{highlightText(match.metadata?.principio_activo || 'N/A', searchTerms)}</td>
                                        <td>{highlightText(match.metadata?.concentracion || 'N/A', searchTerms)}</td>
                                        <td className="match-score">
                                          <div
                                            className="score-badge"
                                            style={{
                                              backgroundColor: getConfidenceColor(match.confidence || Math.round(match.score * 100)),
                                              color: 'white',
                                              padding: '0.25rem 0.5rem',
                                              borderRadius: '4px',
                                              fontWeight: '600',
                                              fontSize: '0.75rem',
                                              cursor: match.ai_reasoning ? 'help' : 'default'
                                            }}
                                            title={match.ai_reasoning || (match.rejection_reason || 'Sin razonamiento disponible')}
                                          >
                                            {match.confidence || Math.round(match.score * 100)}%
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {(activeFilter ? totalProducts : totalUnfilteredProducts) > pagination.limit && (
          <div className="pagination-controls-bottom">
            <button
              className="pagination-btn"
              onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
              disabled={pagination.offset === 0}
            >
              ← Anterior
            </button>
            <span className="pagination-info">
              Página {Math.floor(pagination.offset / pagination.limit) + 1} de {Math.ceil((activeFilter ? totalProducts : totalUnfilteredProducts) / pagination.limit)}
            </span>
            <button
              className="pagination-btn"
              onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
              disabled={pagination.offset + pagination.limit >= (activeFilter ? totalProducts : totalUnfilteredProducts)}
            >
              Siguiente →
            </button>
          </div>
        )}
      </div>

      {/* Modal de confirmación de borrado */}
      {showDeleteModal && ReactDOM.createPortal(
        <>
          <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}></div>
          <div className="modal-container">
            <div className="modal-header">
              <h3>Confirmar eliminación</h3>
              <button className="modal-close-btn" onClick={() => setShowDeleteModal(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div className="modal-content">
              <p>¿Estás seguro de eliminar {getSelectedCount()} match{getSelectedCount() > 1 ? 'es' : ''}?</p>
              <p className="modal-warning">Esta acción no se puede deshacer.</p>
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShowDeleteModal(false)}>Cancelar</button>
              <button className="modal-btn delete" onClick={deleteSelectedMatches}>Eliminar</button>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Modal de mover matches */}
      {showMoveModal && ReactDOM.createPortal(
        <>
          <div className="modal-overlay" onClick={() => setShowMoveModal(false)}></div>
          <div className="modal-container move-modal">
            <div className="modal-header">
              <h3>Mover matches a otro producto</h3>
              <button className="modal-close-btn" onClick={() => setShowMoveModal(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div className="modal-content">
              {!selectedDestination ? (
                <>
                  <p className="modal-info">Moviendo {getSelectedCount()} match{getSelectedCount() > 1 ? 'es' : ''}. Busca el producto destino:</p>
                  <div className="modal-search">
                    <input
                      type="text"
                      placeholder="Buscar producto por código o presentación..."
                      value={moveSearchText}
                      onChange={(e) => {
                        setMoveSearchText(e.target.value);
                        searchMoveDestination(e.target.value);
                      }}
                      autoFocus
                    />
                  </div>
                  {moveSearchResults.length > 0 && (
                    <div className="modal-search-results">
                      {moveSearchResults.map((producto) => (
                        <div
                          key={producto['Codigo Presentación']}
                          className="search-result-item"
                          onClick={() => setSelectedDestination(producto)}
                        >
                          <div className="result-code">{producto['Codigo Presentación']}</div>
                          <div className="result-details">
                            <div className="result-presentacion">{producto['Presentación']}</div>
                            <div className="result-meta">
                              {producto['Product']} - {producto['Corporation']}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {moveSearchText.length >= 2 && moveSearchResults.length === 0 && (
                    <div className="no-results">No se encontraron productos</div>
                  )}
                </>
              ) : (
                <>
                  {isMoving ? (
                    <div className="modal-loading">
                      <div className="spinner"></div>
                      <p>Moviendo {getSelectedCount()} match{getSelectedCount() > 1 ? 'es' : ''} a "{selectedDestination['Presentación']}"...</p>
                    </div>
                  ) : (
                    <div className="move-confirmation">
                      <p className="confirmation-title">¿Confirmar movimiento?</p>
                      <div className="confirmation-details">
                        <div className="confirmation-item">
                          <label>Matches a mover:</label>
                          <span className="highlight-count">{getSelectedCount()} match{getSelectedCount() > 1 ? 'es' : ''}</span>
                        </div>
                        <div className="confirmation-arrow">→</div>
                        <div className="confirmation-item">
                          <label>Producto destino:</label>
                          <div className="destination-info">
                            <span className="destination-code">{selectedDestination['Codigo Presentación']}</span>
                            <span className="destination-name">{selectedDestination['Presentación']}</span>
                            <span className="destination-meta">{selectedDestination['Product']} - {selectedDestination['Corporation']}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        className="btn-change-destination"
                        onClick={() => setSelectedDestination(null)}
                      >
                        ← Cambiar destino
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="modal-btn cancel"
                onClick={() => {
                  setShowMoveModal(false);
                  setMoveSearchText('');
                  setMoveSearchResults([]);
                  setSelectedDestination(null);
                }}
                disabled={isMoving}
              >
                Cancelar
              </button>
              {selectedDestination && !isMoving && (
                <button
                  className="modal-btn confirm-move"
                  onClick={confirmMoveMatches}
                >
                  Mover {getSelectedCount()} match{getSelectedCount() > 1 ? 'es' : ''}
                </button>
              )}
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Modal de Matching en la Nube */}
      {showMatchingModal && ReactDOM.createPortal(
        <>
          <div className="modal-overlay" onClick={() => !matchingJob && setShowMatchingModal(false)}></div>
          <div className="matching-modal">
            <div className="matching-modal-header">
              <h2>Matching con AI (Cloud)</h2>
              {!matchingJob && (
                <button className="modal-close-btn" onClick={() => setShowMatchingModal(false)}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              )}
            </div>

            <div className="matching-modal-content">
              {!matchingJob ? (
                // Configuración
                <div className="matching-config">
                  <div className="config-group">
                    <label>Offset (desde qué producto):</label>
                    <input
                      type="number"
                      value={matchingConfig.offset}
                      onChange={(e) => setMatchingConfig(prev => ({ ...prev, offset: parseInt(e.target.value) }))}
                      min="0"
                      max="10000"
                    />
                    <span className="config-hint">Empezar desde el producto N (para continuar jobs)</span>
                  </div>

                  <div className="config-group">
                    <label>Límite de productos a procesar:</label>
                    <input
                      type="number"
                      value={matchingConfig.limit}
                      onChange={(e) => setMatchingConfig(prev => ({ ...prev, limit: parseInt(e.target.value) }))}
                      min="1"
                      max="200"
                    />
                    <span className="config-hint">Máximo 200 productos por job (recomendado 100)</span>
                  </div>

                  <div className="config-group">
                    <label>Top K candidatos de Pinecone:</label>
                    <input
                      type="number"
                      value={matchingConfig.topK}
                      onChange={(e) => setMatchingConfig(prev => ({ ...prev, topK: parseInt(e.target.value) }))}
                      min="10"
                      max="100"
                    />
                    <span className="config-hint">Entre 10 y 100 candidatos por producto</span>
                  </div>

                  <div className="config-group">
                    <label>Mínimo score vectorial (%):</label>
                    <input
                      type="number"
                      value={matchingConfig.minVectorScore * 100}
                      onChange={(e) => setMatchingConfig(prev => ({ ...prev, minVectorScore: parseFloat(e.target.value) / 100 }))}
                      min="50"
                      max="95"
                      step="5"
                    />
                    <span className="config-hint">Solo candidatos con ≥{(matchingConfig.minVectorScore * 100).toFixed(0)}% similitud vectorial</span>
                  </div>

                  <div className="config-group">
                    <label>Mínimo confidence AI (%):</label>
                    <input
                      type="number"
                      value={matchingConfig.minConfidence}
                      onChange={(e) => setMatchingConfig(prev => ({ ...prev, minConfidence: parseInt(e.target.value) }))}
                      min="70"
                      max="100"
                      step="5"
                    />
                    <span className="config-hint">Solo aprobar matches con ≥{matchingConfig.minConfidence}% confidence</span>
                  </div>

                  <button
                    className="start-matching-btn"
                    onClick={startCloudMatching}
                    disabled={isStartingMatching}
                  >
                    {isStartingMatching ? (
                      <>
                        <span className="spinner"></span>
                        Iniciando...
                      </>
                    ) : (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polygon points="5 3 19 12 5 21 5 3"/>
                        </svg>
                        Iniciar Proceso
                      </>
                    )}
                  </button>
                </div>
              ) : (
                // Logs en tiempo real
                <div className="matching-logs-container">
                  {matchingProgress && (
                    <div className="matching-progress">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${(matchingProgress.processed / matchingProgress.total) * 100}%` }}
                        ></div>
                      </div>
                      <div className="progress-stats">
                        <span>Progreso: {matchingProgress.processed}/{matchingProgress.total}</span>
                        <span>Matches: {matchingProgress.matches}</span>
                        <span>Rechazados: {matchingProgress.rejected}</span>
                      </div>
                    </div>
                  )}

                  <div className="logs-box">
                    {matchingLogs.map((log, idx) => (
                      <div key={idx} className={`log-entry log-${log.type}`}>
                        <span className="log-timestamp">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                        <span className="log-message">{log.message}</span>
                      </div>
                    ))}
                  </div>

                  {matchingJob.status === 'completed' && (
                    <button
                      className="close-modal-btn"
                      onClick={() => {
                        setShowMatchingModal(false);
                        setMatchingJob(null);
                        setMatchingLogs([]);
                        setMatchingProgress(null);
                      }}
                    >
                      Cerrar
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Panel lateral de detalle del producto */}
      {selectedProduct && ReactDOM.createPortal(
        <>
          <div className="detail-overlay" onClick={() => setSelectedProduct(null)}></div>
          <div className="detail-panel">
            <div className="detail-panel-header">
              <h3>Detalle del Producto</h3>
              <button className="detail-close-btn" onClick={() => setSelectedProduct(null)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>

            <div className="detail-panel-content">
              {/* Producto Maestro - Destacado */}
              {selectedProduct.maestro_nombre && (
                <div className="maestro-product-banner">
                  <div className="maestro-label">Producto Maestro:</div>
                  <div className="maestro-name">{selectedProduct.maestro_nombre}</div>
                  {selectedProduct.maestro_marca && (
                    <div className="maestro-marca">Marca: {selectedProduct.maestro_marca}</div>
                  )}
                  <div className="maestro-codigo">Código: {selectedProduct.maestro_codigo}</div>
                </div>
              )}

              {/* Producto Candidato - Para comparar */}
              {selectedProduct.title && (
                <div className="candidate-product-banner">
                  <div className="candidate-label">Producto Candidato:</div>
                  <div className="candidate-name">{selectedProduct.title}</div>
                  {selectedProduct.marca && selectedProduct.marca !== 'N/A' && (
                    <div className="candidate-marca">Marca: {selectedProduct.marca}</div>
                  )}
                </div>
              )}

              {/* Imagen del producto */}
              {selectedProduct.imagen && selectedProduct.imagen !== 'N/A' && (
                <div className="detail-image-container">
                  <img
                    src={selectedProduct.imagen}
                    alt={selectedProduct.title}
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}

              {/* Precios - Compacto en 3 columnas siempre */}
              {(isValidValue(selectedProduct.precio_original) ||
                isValidValue(selectedProduct.precio_descontado) ||
                isValidValue(selectedProduct.precio_venta)) && (
                <div className="pricing-compact-section">
                  <div className="pricing-compact-grid">
                    <div className="pricing-compact-item">
                      <label>Original</label>
                      <span className="price-value price-original">
                        {isValidValue(selectedProduct.precio_original)
                          ? `$${Number(selectedProduct.precio_original).toFixed(0)}`
                          : '-'}
                      </span>
                    </div>
                    <div className="pricing-compact-item">
                      <label>Descontado</label>
                      <span className="price-value price-discounted">
                        {isValidValue(selectedProduct.precio_descontado)
                          ? `$${Number(selectedProduct.precio_descontado).toFixed(0)}`
                          : '-'}
                        {isValidValue(selectedProduct.precio_descontado) && selectedProduct.descuento_porcentaje && (
                          <span className="discount-badge">-{selectedProduct.descuento_porcentaje}%</span>
                        )}
                      </span>
                    </div>
                    <div className="pricing-compact-item">
                      <label>Venta</label>
                      <span className="price-value price-sale">
                        {isValidValue(selectedProduct.precio_venta)
                          ? `$${Number(selectedProduct.precio_venta).toFixed(0)}`
                          : '-'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Razonamiento del Match - Debajo de precios */}
              {selectedProduct.ai_reasoning && selectedProduct.ai_reasoning !== 'Sin razonamiento disponible' && (
                <div className="ai-reasoning-section">
                  <div className="ai-reasoning-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    <h4>Razonamiento del Match (AI)</h4>
                  </div>
                  <div className="ai-reasoning-content">
                    {selectedProduct.ai_reasoning}
                  </div>
                </div>
              )}

              {/* Información principal */}
              <div className="detail-section">
                <h4>Información General</h4>
                <div className="detail-grid">
                  {isValidValue(selectedProduct.title) && (
                    <div className="detail-item full-width">
                      <label>Título:</label>
                      <span className="product-title-bold">{selectedProduct.title}</span>
                    </div>
                  )}
                  {isValidValue(selectedProduct.marca) && (
                    <div className="detail-item">
                      <label>Marca:</label>
                      <span>{selectedProduct.marca}</span>
                    </div>
                  )}
                  {isValidValue(selectedProduct.sitio) && (
                    <div className="detail-item">
                      <label>Sitio:</label>
                      <span>{selectedProduct.sitio}</span>
                    </div>
                  )}
                  {isValidValue(selectedProduct.categoria) && (
                    <div className="detail-item">
                      <label>Categoría:</label>
                      <span>{selectedProduct.categoria}</span>
                    </div>
                  )}
                  {isValidValue(selectedProduct.laboratorio) && (
                    <div className="detail-item">
                      <label>Laboratorio:</label>
                      <span>{selectedProduct.laboratorio}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Stock y SKU - Solo si están disponibles */}
              {(isValidValue(selectedProduct.stock) || isValidValue(selectedProduct.sku)) && (
                <div className="detail-section">
                  <h4>Inventario</h4>
                  <div className="detail-grid">
                    {isValidValue(selectedProduct.stock) && (
                      <div className="detail-item">
                        <label>Stock:</label>
                        <span>{selectedProduct.stock}</span>
                      </div>
                    )}
                    {isValidValue(selectedProduct.sku) && (
                      <div className="detail-item">
                        <label>SKU:</label>
                        <span>{selectedProduct.sku}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Información farmacéutica - Solo mostrar sección si hay al menos un dato */}
              {(isValidValue(selectedProduct.forma_farmaceutica) ||
                isValidValue(selectedProduct.principio_activo) ||
                isValidValue(selectedProduct.concentracion) ||
                isValidValue(selectedProduct.via_administracion) ||
                isValidValue(selectedProduct.presentacion) ||
                isValidValue(selectedProduct.registro_sanitario) ||
                isValidValue(selectedProduct.codigo_barras)) && (
                <div className="detail-section">
                  <h4>Información Farmacéutica</h4>
                  <div className="detail-grid">
                    {isValidValue(selectedProduct.forma_farmaceutica) && (
                      <div className="detail-item">
                        <label>Forma Farmacéutica:</label>
                        <span>{selectedProduct.forma_farmaceutica}</span>
                      </div>
                    )}
                    {isValidValue(selectedProduct.principio_activo) && (
                      <div className="detail-item">
                        <label>Principio Activo:</label>
                        <span>{selectedProduct.principio_activo}</span>
                      </div>
                    )}
                    {isValidValue(selectedProduct.concentracion) && (
                      <div className="detail-item">
                        <label>Concentración:</label>
                        <span>{selectedProduct.concentracion}</span>
                      </div>
                    )}
                    {isValidValue(selectedProduct.via_administracion) && (
                      <div className="detail-item">
                        <label>Vía de Administración:</label>
                        <span>{selectedProduct.via_administracion}</span>
                      </div>
                    )}
                    {isValidValue(selectedProduct.presentacion) && (
                      <div className="detail-item">
                        <label>Presentación:</label>
                        <span>{selectedProduct.presentacion}</span>
                      </div>
                    )}
                    {isValidValue(selectedProduct.registro_sanitario) && (
                      <div className="detail-item">
                        <label>Registro Sanitario:</label>
                        <span>{selectedProduct.registro_sanitario}</span>
                      </div>
                    )}
                    {isValidValue(selectedProduct.codigo_barras) && (
                      <div className="detail-item">
                        <label>Código de Barras:</label>
                        <span>{selectedProduct.codigo_barras}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* URL del producto */}
              {selectedProduct.url && selectedProduct.url !== 'N/A' && (
                <div className="detail-section">
                  <h4>Enlaces</h4>
                  <a
                    href={selectedProduct.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="detail-link"
                  >
                    Ver producto en {selectedProduct.sitio || 'sitio web'} →
                  </a>
                </div>
              )}

              {/* Botón de validar o remover match */}
              {selectedProduct.maestro_codigo && (
                <div className="detail-section detail-actions">
                  {selectedProduct.is_approved ? (
                    // Botón para remover match si ya está aprobado
                    <button
                      className="remove-match-btn"
                      onClick={() => removeMatch(selectedProduct)}
                      title="Remover este match"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      Remover Match
                    </button>
                  ) : (
                    // Botón para validar match si es rechazado o no existe
                    <button
                      className="validate-match-btn"
                      onClick={() => validateManualMatch(selectedProduct)}
                      title="Agregar este producto como match validado manualmente"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                      Validar como Match
                    </button>
                  )}
                  <p className="validate-hint">
                    {selectedProduct.is_approved
                      ? `Este match será removido del producto maestro:`
                      : `Este producto se agregará como match del producto maestro:`}
                    <br/>
                    <strong>{selectedProduct.maestro_nombre}</strong>
                  </p>
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Modal de Todos los Jobs Activos */}
      {showAllJobsModal && ReactDOM.createPortal(
        <>
          <div className="modal-overlay" onClick={() => setShowAllJobsModal(false)}></div>
          <div className="matching-modal">
            <div className="matching-modal-header">
              <h3>📊 Jobs de Matching Activos</h3>
              <button className="modal-close-btn" onClick={() => setShowAllJobsModal(false)}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>
            </div>
            <div className="matching-modal-content" style={{maxHeight: '70vh', overflowY: 'auto'}}>
              {loadingJobs ? (
                <div style={{textAlign: 'center', padding: '2rem'}}>
                  <div className="spinner"></div>
                  <p>Cargando jobs...</p>
                </div>
              ) : allJobs.length === 0 ? (
                <div style={{textAlign: 'center', padding: '2rem', color: '#6b7280'}}>
                  No hay jobs registrados
                </div>
              ) : (
                <div>
                  {allJobs.map(job => {
                    const offset = job.config?.offset || 0;
                    const processed = job.progress?.processed || 0;
                    const total = job.progress?.total || job.config?.limit || 0;
                    const totalProcessed = offset + processed;
                    const percentage = total > 0 ? ((processed / total) * 100).toFixed(1) : 0;
                    const statusColor = job.status === 'running' ? '#10b981' :
                                       job.status === 'completed' ? '#3b82f6' : '#ef4444';

                    return (
                      <div key={job.jobId} style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '1rem',
                        background: '#f9fafb'
                      }}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem'}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                            <span style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              background: statusColor,
                              display: 'inline-block'
                            }}></span>
                            <strong>{job.status.toUpperCase()}</strong>
                          </div>
                          <code style={{fontSize: '0.75rem', color: '#6b7280'}}>{job.jobId}</code>
                        </div>

                        <div style={{fontSize: '0.875rem', color: '#4b5563', marginBottom: '0.5rem'}}>
                          <div>📦 Progreso: {processed}/{total} ({percentage}%)</div>
                          <div>📍 Offset: {offset} → Total procesado: {totalProcessed}</div>
                          <div>✅ Matches: {job.progress?.matches || 0}</div>
                          <div>❌ Rechazados: {job.progress?.rejected || 0}</div>
                        </div>

                        {job.status === 'running' && (
                          <div style={{
                            width: '100%',
                            height: '4px',
                            background: '#e5e7eb',
                            borderRadius: '2px',
                            overflow: 'hidden',
                            marginBottom: '0.5rem'
                          }}>
                            <div style={{
                              width: `${percentage}%`,
                              height: '100%',
                              background: statusColor,
                              transition: 'width 0.3s ease'
                            }}></div>
                          </div>
                        )}

                        <button
                          onClick={() => {
                            setViewingJobId(job.jobId);
                            setShowAllJobsModal(false);
                            viewExistingJobLogs();
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            marginTop: '0.5rem'
                          }}
                        >
                          Ver Logs
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default Matcheo;
